import { useCallback, useRef } from "react";

/**
 * Track the last selection position on a TextField so a variable picker can
 * insert at the caret instead of always appending at the end. Wire `ref` and
 * `onBlur` onto the input; call `insertAtCaret(current, text)` from the picker
 * to get the next string + the caret position for focus restoration.
 */
export function useCaretInsert() {
  const inputRef = useRef<HTMLInputElement>(null);
  const caretRef = useRef<number | null>(null);

  const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    caretRef.current = e.currentTarget.selectionStart;
  }, []);

  const insertAtCaret = useCallback((current: string, text: string) => {
    const pos = caretRef.current ?? current.length;
    const safePos = Math.max(0, Math.min(pos, current.length));
    const next = current.slice(0, safePos) + text + current.slice(safePos);
    const newCaret = safePos + text.length;
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try { el.setSelectionRange(newCaret, newCaret); } catch { /* ignore */ }
    });
    caretRef.current = newCaret;
    return next;
  }, []);

  return { inputRef, onBlur, insertAtCaret };
}
